import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import RichTextEditor from '../components/RichTextEditor';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import * as LucideIcons from 'lucide-react';
import { 
  LogOut, Home, Settings, Image, MapPin, Star, Plus, Edit, Trash2, 
  Save, X, ChevronLeft, ChevronRight, Eye, Upload, FileText, Sparkles,
  Bath, Waves, ThermometerSun, ExternalLink, Video, FileImage, Check, FolderPlus,
  Users, Key, ShieldCheck, Menu, ArrowUp, ArrowDown, UserPlus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon Preview Component - renders Lucide icons by name
const IconPreview = ({ name, className }) => {
  // Convert kebab-case to PascalCase for Lucide icons
  const iconName = name.split('-').map(part => 
    part.charAt(0).toUpperCase() + part.slice(1)
  ).join('');
  
  const IconComponent = LucideIcons[iconName];
  
  if (IconComponent) {
    return <IconComponent className={className} />;
  }
  
  // Fallback to Star if icon not found
  return <Star className={className} />;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('content');
  
  // Data states
  const [accommodations, setAccommodations] = useState([]);
  const [attractions, setAttractions] = useState([]);
  const [features, setFeatures] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [siteContent, setSiteContent] = useState({});
  const [contentBlocks, setContentBlocks] = useState([]);
  const [availableIcons, setAvailableIcons] = useState({ icons: [], categories: [] });
  
  // Edit modals
  const [editAccommodation, setEditAccommodation] = useState(null);
  const [editAttraction, setEditAttraction] = useState(null);
  const [editFeature, setEditFeature] = useState(null);
  const [editAmenity, setEditAmenity] = useState(null);
  const [editGalleryImage, setEditGalleryImage] = useState(null);
  const [showNewAccommodation, setShowNewAccommodation] = useState(false);
  const [showNewAttraction, setShowNewAttraction] = useState(false);
  const [showNewFeature, setShowNewFeature] = useState(false);
  const [showNewAmenity, setShowNewAmenity] = useState(false);
  const [showNewGalleryImage, setShowNewGalleryImage] = useState(false);
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  
  // Settings states
  const [adminUsers, setAdminUsers] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false });
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [newUserData, setNewUserData] = useState({ username: '', email: '', password: '' });
  const [smtpConfig, setSmtpConfig] = useState({ host: '', port: 587, username: '', password: '', from_email: '', from_name: 'Agromiedzierza CMS', use_tls: true });
  const [smtpTestEmail, setSmtpTestEmail] = useState('');
  const [isSavingSmtp, setIsSavingSmtp] = useState(false);
  const [showNewUser, setShowNewUser] = useState(false);
  const [reviewsConfig, setReviewsConfig] = useState({ enabled: true, average_rating: 5.0, total_reviews: 0 });
  const [reviews, setReviews] = useState([]);
  const [showNewReview, setShowNewReview] = useState(false);
  const [newReview, setNewReview] = useState({ author_name: '', rating: 5, text: '', time: new Date().toISOString(), profile_photo_url: '' });
  const [promoConfig, setPromoConfig] = useState({
    enabled: false,
    title: '🎁 Specjalna oferta!',
    subtitle: 'Tylko dla nowych gości',
    description: 'Zarezerwuj pobyt teraz i otrzymaj 10% zniżki na pierwszą wizytę!',
    button_text: 'Skorzystaj z oferty',
    button_link: '#contact',
    discount_code: '',
    background_color: '#1A3C34',
    show_after_seconds: 5,
    show_once_per_session: true
  });
  const [seoConfig, setSeoConfig] = useState({
    meta_title: 'VIP Village Agromiedzierza - Agroturystyka w Świętokrzyskim',
    meta_description: 'Luksusowy wypoczynek w sercu natury. Domki, stodoły i apartamenty bezpośrednio przy lesie.',
    meta_keywords: 'agroturystyka, świętokrzyskie, domki, nocleg',
    og_title: '',
    og_description: '',
    og_image: '',
    canonical_url: '',
    robots: 'index, follow',
    google_site_verification: ''
  });
  const [imageAltTexts, setImageAltTexts] = useState({});
  const [socialLinks, setSocialLinks] = useState([]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [accRes, attrRes, featRes, amenRes, galleryRes, contentRes, iconsRes, catRes, usersRes, menuRes, twoFARes, smtpRes, reviewsRes, reviewsConfigRes, promoRes, seoRes, altRes, socialRes] = await Promise.all([
        axios.get(`${API}/accommodations`),
        axios.get(`${API}/attractions`),
        axios.get(`${API}/features`),
        axios.get(`${API}/amenities`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/site-content/all`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/icons`).catch(() => ({ data: { icons: [], categories: [] } })),
        axios.get(`${API}/gallery-categories`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/menu-items`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/2fa/status`, { headers: getAuthHeaders() }).catch(() => ({ data: { enabled: false } })),
        axios.get(`${API}/smtp-config`, { headers: getAuthHeaders() }).catch(() => ({ data: {} })),
        axios.get(`${API}/reviews`).catch(() => ({ data: { reviews: [] } })),
        axios.get(`${API}/reviews/config/admin`, { headers: getAuthHeaders() }).catch(() => ({ data: { enabled: true } })),
        axios.get(`${API}/promo-popup`).catch(() => ({ data: {} })),
        axios.get(`${API}/seo-config`).catch(() => ({ data: {} })),
        axios.get(`${API}/image-alt-texts`).catch(() => ({ data: {} })),
        axios.get(`${API}/social-links`).catch(() => ({ data: [] })),
      ]);
      
      setAccommodations(accRes.data);
      setAttractions(attrRes.data);
      setFeatures(featRes.data);
      setAmenities(amenRes.data);
      setGallery(galleryRes.data);
      setContentBlocks(contentRes.data);
      setAvailableIcons(iconsRes.data);
      setGalleryCategories(catRes.data);
      setAdminUsers(usersRes.data);
      setMenuItems(menuRes.data);
      setTwoFAStatus(twoFARes.data);
      if (smtpRes.data && Object.keys(smtpRes.data).length > 0) {
        setSmtpConfig({ ...smtpConfig, ...smtpRes.data });
      }
      setReviews(reviewsRes.data.reviews || []);
      setReviewsConfig(reviewsConfigRes.data);
      if (promoRes.data && Object.keys(promoRes.data).length > 0) {
        setPromoConfig({ ...promoConfig, ...promoRes.data });
      }
      if (seoRes.data && Object.keys(seoRes.data).length > 0) {
        setSeoConfig({ ...seoConfig, ...seoRes.data });
      }
      if (altRes.data) {
        setImageAltTexts(altRes.data);
      }
      setSocialLinks(socialRes.data || []);
      
      // Convert content blocks to object
      const contentObj = {};
      contentRes.data.forEach(block => {
        contentObj[block.key] = block.value;
      });
      setSiteContent(contentObj);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
      toast.error('Błąd ładowania danych');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin');
  };

  // Content Block handlers
  const handleSaveContent = async (key, value) => {
    try {
      await axios.put(`${API}/site-content/${key}`, { value }, { headers: getAuthHeaders() });
      setSiteContent(prev => ({ ...prev, [key]: value }));
      toast.success('Treść zapisana');
    } catch (error) {
      toast.error('Błąd zapisu');
    }
  };

  // Accommodation CRUD
  const handleSaveAccommodation = async (data, isNew = false) => {
    try {
      if (isNew) {
        await axios.post(`${API}/accommodations`, data, { headers: getAuthHeaders() });
        toast.success('Obiekt dodany');
      } else {
        await axios.put(`${API}/accommodations/${data.id}`, data, { headers: getAuthHeaders() });
        toast.success('Obiekt zaktualizowany');
      }
      fetchData();
      setEditAccommodation(null);
      setShowNewAccommodation(false);
    } catch (error) {
      toast.error('Błąd zapisu obiektu');
    }
  };

  const handleDeleteAccommodation = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten obiekt?')) return;
    try {
      await axios.delete(`${API}/accommodations/${id}`, { headers: getAuthHeaders() });
      toast.success('Obiekt usunięty');
      fetchData();
    } catch (error) {
      toast.error('Błąd usuwania obiektu');
    }
  };

  // Attractions CRUD
  const handleSaveAttraction = async (data, isNew = false) => {
    try {
      if (isNew) {
        await axios.post(`${API}/attractions`, data, { headers: getAuthHeaders() });
        toast.success('Atrakcja dodana');
      } else {
        await axios.put(`${API}/attractions/${data.id}`, data, { headers: getAuthHeaders() });
        toast.success('Atrakcja zaktualizowana');
      }
      fetchData();
      setEditAttraction(null);
      setShowNewAttraction(false);
    } catch (error) {
      toast.error('Błąd zapisu atrakcji');
    }
  };

  const handleDeleteAttraction = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę atrakcję?')) return;
    try {
      await axios.delete(`${API}/attractions/${id}`, { headers: getAuthHeaders() });
      toast.success('Atrakcja usunięta');
      fetchData();
    } catch (error) {
      toast.error('Błąd usuwania atrakcji');
    }
  };

  // Features CRUD
  const handleSaveFeature = async (data, isNew = false) => {
    try {
      if (isNew) {
        await axios.post(`${API}/features`, data, { headers: getAuthHeaders() });
        toast.success('Atut dodany');
      } else {
        await axios.put(`${API}/features/${data.id}`, data, { headers: getAuthHeaders() });
        toast.success('Atut zaktualizowany');
      }
      fetchData();
      setEditFeature(null);
      setShowNewFeature(false);
    } catch (error) {
      toast.error('Błąd zapisu atutu');
    }
  };

  const handleDeleteFeature = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten atut?')) return;
    try {
      await axios.delete(`${API}/features/${id}`, { headers: getAuthHeaders() });
      toast.success('Atut usunięty');
      fetchData();
    } catch (error) {
      toast.error('Błąd usuwania atutu');
    }
  };

  // Amenities CRUD
  const handleSaveAmenity = async (data, isNew = false) => {
    try {
      if (isNew) {
        await axios.post(`${API}/amenities`, data, { headers: getAuthHeaders() });
        toast.success('Udogodnienie dodane');
      } else {
        await axios.put(`${API}/amenities/${data.id}`, data, { headers: getAuthHeaders() });
        toast.success('Udogodnienie zaktualizowane');
      }
      fetchData();
      setEditAmenity(null);
      setShowNewAmenity(false);
    } catch (error) {
      toast.error('Błąd zapisu udogodnienia');
    }
  };

  const handleDeleteAmenity = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to udogodnienie?')) return;
    try {
      await axios.delete(`${API}/amenities/${id}`, { headers: getAuthHeaders() });
      toast.success('Udogodnienie usunięte');
      fetchData();
    } catch (error) {
      toast.error('Błąd usuwania udogodnienia');
    }
  };

  // Gallery CRUD
  const handleSaveGalleryImage = async (data, isNew = false) => {
    try {
      if (isNew) {
        await axios.post(`${API}/gallery`, data, { headers: getAuthHeaders() });
        toast.success('Zdjęcie dodane');
      } else {
        await axios.put(`${API}/gallery/${data.id}`, data, { headers: getAuthHeaders() });
        toast.success('Zdjęcie zaktualizowane');
      }
      fetchData();
      setEditGalleryImage(null);
      setShowNewGalleryImage(false);
    } catch (error) {
      toast.error('Błąd zapisu zdjęcia');
    }
  };

  const handleDeleteGalleryImage = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to zdjęcie?')) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers: getAuthHeaders() });
      toast.success('Zdjęcie usunięte');
      fetchData();
    } catch (error) {
      toast.error('Błąd usuwania zdjęcia');
    }
  };

  const moveGalleryImage = async (fromIndex, toIndex) => {
    const newGallery = [...gallery];
    const [movedImage] = newGallery.splice(fromIndex, 1);
    newGallery.splice(toIndex, 0, movedImage);
    
    // Update order field for each image
    const updatedGallery = newGallery.map((img, idx) => ({ ...img, order: idx }));
    setGallery(updatedGallery);
    
    // Save new order to backend
    try {
      const payload = updatedGallery.map((img, idx) => ({ id: img.id, order: idx }));
      console.log('Reorder payload:', payload.length, 'items');
      const response = await axios.post(`${API}/gallery-reorder`, payload, { headers: getAuthHeaders() });
      console.log('Reorder response:', response.data);
      toast.success('Kolejność zapisana');
    } catch (error) {
      console.error('Reorder error:', error.response?.data || error.message);
      toast.error('Błąd zapisu kolejności: ' + (error.response?.data?.detail || error.message));
      fetchData(); // Restore original order
    }
  };

  // Gallery Categories
  const handleAddCategory = async () => {
    if (!newCategoryName.trim() || !newCategoryLabel.trim()) {
      toast.error('Podaj nazwę i etykietę kategorii');
      return;
    }
    try {
      await axios.post(`${API}/gallery-categories`, { 
        name: newCategoryName.toLowerCase().replace(/\s+/g, '-'),
        label: newCategoryLabel 
      }, { headers: getAuthHeaders() });
      toast.success('Kategoria dodana');
      setNewCategoryName('');
      setNewCategoryLabel('');
      setShowNewCategory(false);
      fetchData();
    } catch (error) {
      toast.error('Błąd dodawania kategorii');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę kategorię?')) return;
    try {
      await axios.delete(`${API}/gallery-categories/${id}`, { headers: getAuthHeaders() });
      toast.success('Kategoria usunięta');
      fetchData();
    } catch (error) {
      toast.error('Błąd usuwania kategorii');
    }
  };

  // Settings handlers
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Hasła nie są identyczne');
      return;
    }
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { headers: getAuthHeaders() });
      toast.success('Hasło zmienione');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd zmiany hasła');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, newUserData, { headers: getAuthHeaders() });
      toast.success('Użytkownik utworzony');
      setNewUserData({ username: '', email: '', password: '' });
      setShowNewUser(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd tworzenia użytkownika');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tego użytkownika?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeaders() });
      toast.success('Użytkownik usunięty');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd usuwania użytkownika');
    }
  };

  const handleSetup2FA = async () => {
    try {
      const res = await axios.post(`${API}/admin/2fa/setup`, {}, { headers: getAuthHeaders() });
      setTwoFASetup(res.data);
    } catch (error) {
      toast.error('Błąd konfiguracji 2FA');
    }
  };

  const handleEnable2FA = async (code) => {
    try {
      await axios.post(`${API}/admin/2fa/enable`, { totp_code: code }, { headers: getAuthHeaders() });
      toast.success('2FA włączone');
      setTwoFASetup(null);
      setTwoFAStatus({ enabled: true });
    } catch (error) {
      toast.error('Nieprawidłowy kod');
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Czy na pewno chcesz wyłączyć uwierzytelnianie dwuskładnikowe?')) return;
    try {
      await axios.post(`${API}/admin/2fa/disable`, {}, { headers: getAuthHeaders() });
      toast.success('2FA wyłączone');
      setTwoFAStatus({ enabled: false });
    } catch (error) {
      toast.error('Błąd wyłączania 2FA');
    }
  };

  const handleSaveMenuItems = async () => {
    try {
      await axios.put(`${API}/menu-items`, menuItems, { headers: getAuthHeaders() });
      toast.success('Menu zapisane');
    } catch (error) {
      toast.error('Błąd zapisywania menu');
    }
  };

  const moveMenuItem = (index, direction) => {
    const newItems = [...menuItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    newItems.forEach((item, i) => item.order = i);
    setMenuItems(newItems);
  };

  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    setIsSavingSmtp(true);
    try {
      await axios.post(`${API}/smtp-config`, smtpConfig, { headers: getAuthHeaders() });
      toast.success('Konfiguracja SMTP zapisana');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd zapisywania SMTP');
    } finally {
      setIsSavingSmtp(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!smtpTestEmail) {
      toast.error('Wprowadź adres email');
      return;
    }
    try {
      await axios.post(`${API}/smtp-test`, { to_email: smtpTestEmail }, { headers: getAuthHeaders() });
      toast.success(`Email testowy wysłany na ${smtpTestEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd wysyłania emaila testowego');
    }
  };

  const handleToggleReviews = async (enabled) => {
    try {
      await axios.put(`${API}/reviews/config`, { ...reviewsConfig, enabled }, { headers: getAuthHeaders() });
      setReviewsConfig({ ...reviewsConfig, enabled });
      toast.success(enabled ? 'Opinie włączone' : 'Opinie wyłączone');
    } catch (error) {
      toast.error('Błąd zmiany ustawień');
    }
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/reviews`, newReview, { headers: getAuthHeaders() });
      toast.success('Opinia dodana');
      setNewReview({ author_name: '', rating: 5, text: '', time: new Date().toISOString(), profile_photo_url: '' });
      setShowNewReview(false);
      fetchData();
    } catch (error) {
      toast.error('Błąd dodawania opinii');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę opinię?')) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, { headers: getAuthHeaders() });
      toast.success('Opinia usunięta');
      fetchData();
    } catch (error) {
      toast.error('Błąd usuwania opinii');
    }
  };

  const handleSavePromoConfig = async () => {
    try {
      await axios.put(`${API}/promo-popup`, promoConfig, { headers: getAuthHeaders() });
      toast.success('Popup promocyjny zapisany');
    } catch (error) {
      toast.error('Błąd zapisu popupu');
    }
  };

  const handleSaveSeoConfig = async () => {
    try {
      await axios.put(`${API}/seo-config`, seoConfig, { headers: getAuthHeaders() });
      toast.success('Konfiguracja SEO zapisana');
    } catch (error) {
      toast.error('Błąd zapisu SEO');
    }
  };

  const handleSaveImageAltText = async (imageKey, altText) => {
    try {
      await axios.put(`${API}/image-alt-texts/${imageKey}`, { alt_text: altText }, { headers: getAuthHeaders() });
      setImageAltTexts({ ...imageAltTexts, [imageKey]: altText });
      toast.success('Tekst alt zapisany');
    } catch (error) {
      toast.error('Błąd zapisu tekstu alt');
    }
  };

  const handleSaveSocialLinks = async () => {
    try {
      const res = await axios.put(`${API}/social-links`, socialLinks, { headers: getAuthHeaders() });
      setSocialLinks(res.data);
      toast.success('Linki social media zapisane');
    } catch (error) {
      toast.error('Błąd zapisu linków social media');
    }
  };

  const updateSocialLink = (id, field, value) => {
    setSocialLinks(socialLinks.map(link => link.id === id ? { ...link, [field]: value } : link));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="animate-pulse font-heading text-2xl text-forest">Ładowanie...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-forest text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <img 
                src={siteContent.site_logo || "https://customer-assets.emergentagent.com/job_rustic-retreat-7/artifacts/gtmkak5t_5556a.png"}
                alt="Logo" 
                className="h-10 w-auto object-contain"
              />
            </Link>
            <div>
              <h1 className="font-heading text-xl">Panel CMS</h1>
              <p className="font-body text-sm text-white/60">
                Zarządzaj treścią strony
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" target="_blank">
              <Button variant="ghost" className="text-white hover:bg-white/10 gap-2">
                <Eye className="w-4 h-4" /> Podgląd strony
              </Button>
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Wyloguj
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-soft flex-wrap h-auto p-2">
            <TabsTrigger value="content" className="gap-2">
              <FileText className="w-4 h-4" />
              Treści
            </TabsTrigger>
            <TabsTrigger value="spa" className="gap-2">
              <Bath className="w-4 h-4" />
              Balia/Sauna
            </TabsTrigger>
            <TabsTrigger value="accommodations" className="gap-2">
              <Home className="w-4 h-4" />
              Obiekty
            </TabsTrigger>
            <TabsTrigger value="attractions" className="gap-2">
              <MapPin className="w-4 h-4" />
              Atrakcje
            </TabsTrigger>
            <TabsTrigger value="amenities" className="gap-2">
              <Sparkles className="w-4 h-4" />
              Udogodnienia
            </TabsTrigger>
            <TabsTrigger value="features" className="gap-2">
              <Star className="w-4 h-4" />
              Atuty
            </TabsTrigger>
            <TabsTrigger value="gallery" className="gap-2">
              <Image className="w-4 h-4" />
              Galeria
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="w-4 h-4" />
              Ustawienia
            </TabsTrigger>
          </TabsList>

          {/* Content Management */}
          <TabsContent value="content">
            <div className="grid gap-6">
              {/* Site Logo */}
              <ContentSection 
                title="Logo strony" 
                description="Logo wyświetlane w nawigacji i stopce"
                icon={<Image className="w-5 h-5" />}
              >
                <ContentField 
                  label="Logo (URL)" 
                  fieldKey="site_logo" 
                  value={siteContent.site_logo || ''} 
                  onSave={handleSaveContent}
                  type="image"
                />
              </ContentSection>
              
              {/* Hero Section */}
              <ContentSection 
                title="Sekcja Hero" 
                description="Główny nagłówek strony"
                icon={<ExternalLink className="w-5 h-5" />}
              >
                <ContentField 
                  label="Tytuł główny" 
                  fieldKey="hero_title" 
                  value={siteContent.hero_title || ''} 
                  onSave={handleSaveContent}
                />
                <ContentField 
                  label="Podtytuł" 
                  fieldKey="hero_subtitle" 
                  value={siteContent.hero_subtitle || ''} 
                  onSave={handleSaveContent}
                  multiline
                />
                <ContentFieldWithUpload 
                  label="Zdjęcie tła" 
                  fieldKey="hero_image" 
                  value={siteContent.hero_image || ''} 
                  onSave={handleSaveContent}
                  type="image"
                  getAuthHeaders={getAuthHeaders}
                />
              </ContentSection>

              {/* About Section */}
              <ContentSection 
                title="Sekcja O nas" 
                description="Informacje o firmie"
              >
                <ContentField 
                  label="Etykieta" 
                  fieldKey="about_label" 
                  value={siteContent.about_label || ''} 
                  onSave={handleSaveContent}
                />
                <ContentField 
                  label="Tytuł" 
                  fieldKey="about_title" 
                  value={siteContent.about_title || ''} 
                  onSave={handleSaveContent}
                />
                <ContentField 
                  label="Tekst 1" 
                  fieldKey="about_text1" 
                  value={siteContent.about_text1 || ''} 
                  onSave={handleSaveContent}
                  multiline
                />
                <ContentField 
                  label="Tekst 2" 
                  fieldKey="about_text2" 
                  value={siteContent.about_text2 || ''} 
                  onSave={handleSaveContent}
                  multiline
                />
                <div className="grid grid-cols-2 gap-4">
                  <ContentFieldWithUpload 
                    label="Zdjęcie 1" 
                    fieldKey="about_image1" 
                    value={siteContent.about_image1 || ''} 
                    onSave={handleSaveContent}
                    type="image"
                    getAuthHeaders={getAuthHeaders}
                  />
                  <ContentFieldWithUpload 
                    label="Zdjęcie 2" 
                    fieldKey="about_image2" 
                    value={siteContent.about_image2 || ''} 
                    onSave={handleSaveContent}
                    type="image"
                    getAuthHeaders={getAuthHeaders}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <ContentField 
                    label="Liczba w statystyce" 
                    fieldKey="about_stat_number" 
                    value={siteContent.about_stat_number || ''} 
                    onSave={handleSaveContent}
                  />
                  <ContentField 
                    label="Tekst statystyki" 
                    fieldKey="about_stat_text" 
                    value={siteContent.about_stat_text || ''} 
                    onSave={handleSaveContent}
                  />
                </div>
              </ContentSection>

              {/* SPA Section */}
              <ContentSection 
                title="Sekcja Balia • Basen • Sauna" 
                description="Informacje o strefie wellness"
                icon={<Bath className="w-5 h-5" />}
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <ContentField 
                    label="Balia - opis" 
                    fieldKey="balia_text" 
                    value={siteContent.balia_text || ''} 
                    onSave={handleSaveContent}
                    multiline
                  />
                  <ContentField 
                    label="Balia - cena" 
                    fieldKey="balia_price" 
                    value={siteContent.balia_price || ''} 
                    onSave={handleSaveContent}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <ContentField 
                    label="Basen - opis" 
                    fieldKey="basen_text" 
                    value={siteContent.basen_text || ''} 
                    onSave={handleSaveContent}
                    multiline
                  />
                  <ContentField 
                    label="Basen - cena" 
                    fieldKey="basen_price" 
                    value={siteContent.basen_price || ''} 
                    onSave={handleSaveContent}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <ContentField 
                    label="Sauna - opis" 
                    fieldKey="sauna_text" 
                    value={siteContent.sauna_text || ''} 
                    onSave={handleSaveContent}
                    multiline
                  />
                  <ContentField 
                    label="Sauna - cena" 
                    fieldKey="sauna_price" 
                    value={siteContent.sauna_price || ''} 
                    onSave={handleSaveContent}
                  />
                </div>
              </ContentSection>

              {/* Contact Section */}
              <ContentSection 
                title="Sekcja Kontakt" 
                description="Dane kontaktowe"
              >
                <ContentField 
                  label="Adres" 
                  fieldKey="address" 
                  value={siteContent.address || ''} 
                  onSave={handleSaveContent}
                />
                <ContentField 
                  label="Telefon" 
                  fieldKey="phone" 
                  value={siteContent.phone || ''} 
                  onSave={handleSaveContent}
                />
                <ContentField 
                  label="Email" 
                  fieldKey="email" 
                  value={siteContent.email || ''} 
                  onSave={handleSaveContent}
                />
              </ContentSection>

              {/* Section Headers */}
              <ContentSection 
                title="Nagłówki sekcji" 
                description="Etykiety i tytuły wszystkich sekcji na stronie głównej"
              >
                <div className="grid md:grid-cols-2 gap-4 p-4 bg-sand/30 rounded-lg">
                  <div className="md:col-span-2">
                    <p className="font-heading text-forest text-sm mb-3">Sekcja: Oferta</p>
                  </div>
                  <ContentField label="Etykieta" fieldKey="offer_label" value={siteContent.offer_label || ''} onSave={handleSaveContent} />
                  <ContentField label="Tytuł" fieldKey="offer_title" value={siteContent.offer_title || ''} onSave={handleSaveContent} />
                  <div className="md:col-span-2">
                    <ContentField label="Podtytuł" fieldKey="offer_subtitle" value={siteContent.offer_subtitle || ''} onSave={handleSaveContent} />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 p-4 bg-sand/30 rounded-lg">
                  <div className="md:col-span-2">
                    <p className="font-heading text-forest text-sm mb-3">Sekcja: Udogodnienia</p>
                  </div>
                  <ContentField label="Etykieta" fieldKey="amenities_label" value={siteContent.amenities_label || ''} onSave={handleSaveContent} />
                  <ContentField label="Tytuł" fieldKey="amenities_title" value={siteContent.amenities_title || ''} onSave={handleSaveContent} />
                </div>

                <div className="grid md:grid-cols-2 gap-4 p-4 bg-sand/30 rounded-lg">
                  <div className="md:col-span-2">
                    <p className="font-heading text-forest text-sm mb-3">Sekcja: Galeria</p>
                  </div>
                  <ContentField label="Etykieta" fieldKey="gallery_label" value={siteContent.gallery_label || ''} onSave={handleSaveContent} />
                  <ContentField label="Tytuł" fieldKey="gallery_title" value={siteContent.gallery_title || ''} onSave={handleSaveContent} />
                  <div className="md:col-span-2">
                    <ContentField label="Podtytuł" fieldKey="gallery_subtitle" value={siteContent.gallery_subtitle || ''} onSave={handleSaveContent} />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 p-4 bg-sand/30 rounded-lg">
                  <div className="md:col-span-2">
                    <p className="font-heading text-forest text-sm mb-3">Sekcja: Balia • Basen • Sauna</p>
                  </div>
                  <ContentField label="Etykieta" fieldKey="spa_label" value={siteContent.spa_label || ''} onSave={handleSaveContent} />
                  <ContentField label="Tytuł" fieldKey="spa_title" value={siteContent.spa_title || ''} onSave={handleSaveContent} />
                  <ContentField label="Nazwa: Balia" fieldKey="balia_name" value={siteContent.balia_name || ''} onSave={handleSaveContent} />
                  <ContentField label="Nazwa: Basen" fieldKey="basen_name" value={siteContent.basen_name || ''} onSave={handleSaveContent} />
                  <ContentField label="Nazwa: Sauna" fieldKey="sauna_name" value={siteContent.sauna_name || ''} onSave={handleSaveContent} />
                </div>

                <div className="grid md:grid-cols-2 gap-4 p-4 bg-sand/30 rounded-lg">
                  <div className="md:col-span-2">
                    <p className="font-heading text-forest text-sm mb-3">Sekcja: Atrakcje</p>
                  </div>
                  <ContentField label="Etykieta" fieldKey="attractions_label" value={siteContent.attractions_label || ''} onSave={handleSaveContent} />
                  <ContentField label="Tytuł" fieldKey="attractions_title" value={siteContent.attractions_title || ''} onSave={handleSaveContent} />
                </div>

                <div className="grid md:grid-cols-2 gap-4 p-4 bg-sand/30 rounded-lg">
                  <div className="md:col-span-2">
                    <p className="font-heading text-forest text-sm mb-3">Sekcja: Kontakt</p>
                  </div>
                  <ContentField label="Etykieta" fieldKey="contact_label" value={siteContent.contact_label || ''} onSave={handleSaveContent} />
                  <ContentField label="Tytuł" fieldKey="contact_title" value={siteContent.contact_title || ''} onSave={handleSaveContent} />
                  <WysiwygField 
                    label="Nasza historia (wyświetla się pod danymi kontaktowymi)" 
                    fieldKey="our_history" 
                    value={siteContent.our_history || ''} 
                    onSave={handleSaveContent}
                  />
                </div>
              </ContentSection>

              {/* Regulamin Section */}
              <ContentSection 
                title="Regulamin" 
                description="Zasady pobytu - edytor WYSIWYG"
              >
                <WysiwygField 
                  label="Treść regulaminu" 
                  fieldKey="regulamin" 
                  value={siteContent.regulamin || ''} 
                  onSave={handleSaveContent}
                />
              </ContentSection>
            </div>
          </TabsContent>

          {/* SPA Page Management */}
          <TabsContent value="spa">
            <div className="grid gap-6">
              <ContentSection title="Strona Balia • Basen • Sauna" description="Edytuj treści strony /spa" icon={<Bath className="w-5 h-5" />}>
                <ContentFieldWithUpload label="Tytuł strony" fieldKey="spa_page_title" value={siteContent.spa_page_title || ''} onSave={handleSaveContent} getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Wprowadzenie" fieldKey="spa_page_intro" value={siteContent.spa_page_intro || ''} onSave={handleSaveContent} multiline getAuthHeaders={getAuthHeaders} />
              </ContentSection>
              
              <ContentSection title="Gorąca Balia" description="Sekcja balii" icon={<Bath className="w-5 h-5" />}>
                <ContentFieldWithUpload label="Tytuł" fieldKey="balia_title" value={siteContent.balia_title || ''} onSave={handleSaveContent} getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Podtytuł" fieldKey="balia_subtitle" value={siteContent.balia_subtitle || ''} onSave={handleSaveContent} getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Opis" fieldKey="balia_description" value={siteContent.balia_description || ''} onSave={handleSaveContent} multiline getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Historia" fieldKey="balia_history" value={siteContent.balia_history || ''} onSave={handleSaveContent} multiline getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Korzyści (oddziel |)" fieldKey="balia_benefits" value={siteContent.balia_benefits || ''} onSave={handleSaveContent} multiline getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Cennik" fieldKey="balia_full_price" value={siteContent.balia_full_price || ''} onSave={handleSaveContent} multiline getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Zdjęcie" fieldKey="balia_image" value={siteContent.balia_image || ''} onSave={handleSaveContent} type="image" getAuthHeaders={getAuthHeaders} />
              </ContentSection>
              
              <ContentSection title="Sauna Fińska" description="Sekcja sauny" icon={<ThermometerSun className="w-5 h-5" />}>
                <ContentFieldWithUpload label="Tytuł" fieldKey="sauna_title" value={siteContent.sauna_title || ''} onSave={handleSaveContent} getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Podtytuł" fieldKey="sauna_subtitle" value={siteContent.sauna_subtitle || ''} onSave={handleSaveContent} getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Opis" fieldKey="sauna_description" value={siteContent.sauna_description || ''} onSave={handleSaveContent} multiline getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Zalety (oddziel |)" fieldKey="sauna_benefits" value={siteContent.sauna_benefits || ''} onSave={handleSaveContent} multiline getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Cennik" fieldKey="sauna_full_price" value={siteContent.sauna_full_price || ''} onSave={handleSaveContent} multiline getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Zdjęcie" fieldKey="sauna_image" value={siteContent.sauna_image || ''} onSave={handleSaveContent} type="image" getAuthHeaders={getAuthHeaders} />
              </ContentSection>
              
              <ContentSection title="Basen Premium" description="Sekcja basenu" icon={<Waves className="w-5 h-5" />}>
                <ContentFieldWithUpload label="Tytuł" fieldKey="basen_title" value={siteContent.basen_title || ''} onSave={handleSaveContent} getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Podtytuł" fieldKey="basen_subtitle" value={siteContent.basen_subtitle || ''} onSave={handleSaveContent} getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Opis" fieldKey="basen_description" value={siteContent.basen_description || ''} onSave={handleSaveContent} multiline getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Cechy (oddziel |)" fieldKey="basen_features" value={siteContent.basen_features || ''} onSave={handleSaveContent} multiline getAuthHeaders={getAuthHeaders} />
                <ContentFieldWithUpload label="Zdjęcie" fieldKey="basen_image" value={siteContent.basen_image || ''} onSave={handleSaveContent} type="image" getAuthHeaders={getAuthHeaders} />
              </ContentSection>
            </div>
          </TabsContent>

          {/* Accommodations Management */}
          <TabsContent value="accommodations">
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-xl text-forest">Zarządzanie obiektami</CardTitle>
                    <CardDescription>Dodawaj, edytuj i usuwaj zakwaterowania</CardDescription>
                  </div>
                  <Button onClick={() => setShowNewAccommodation(true)} className="bg-terracotta hover:bg-terracotta-dark gap-2">
                    <Plus className="w-4 h-4" /> Dodaj obiekt
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {accommodations.map((acc) => (
                    <div key={acc.id} className="bg-white p-4 rounded shadow-soft">
                      <div className="relative h-40 mb-4 overflow-hidden rounded">
                        <img 
                          src={acc.images?.[0] || 'https://via.placeholder.com/400x300'} 
                          alt={acc.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-2 right-2 flex gap-1">
                          <Button size="sm" variant="secondary" onClick={() => setEditAccommodation(acc)} className="h-8 w-8 p-0">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteAccommodation(acc.id)} className="h-8 w-8 p-0">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        {acc.images?.length > 1 && (
                          <div className="absolute bottom-2 left-2 bg-black/60 text-white px-2 py-1 rounded text-xs">
                            <Image className="w-3 h-3 inline mr-1" />{acc.images.length} zdjęć
                          </div>
                        )}
                      </div>
                      <h3 className="font-heading text-lg text-forest">{acc.name}</h3>
                      <p className="font-body text-sm text-forest/60 mt-1 line-clamp-2">{acc.description}</p>
                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-sand-dark">
                        <span className="font-body text-sm text-forest/60">do {acc.capacity} osób</span>
                        <span className="font-heading text-terracotta">{acc.price_low_season} - {acc.price_high_season} zł</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attractions Management */}
          <TabsContent value="attractions">
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-xl text-forest">Zarządzanie atrakcjami</CardTitle>
                    <CardDescription>Atrakcje na terenie i w pobliżu</CardDescription>
                  </div>
                  <Button onClick={() => setShowNewAttraction(true)} className="bg-terracotta hover:bg-terracotta-dark gap-2">
                    <Plus className="w-4 h-4" /> Dodaj atrakcję
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Onsite attractions */}
                  <div>
                    <h3 className="font-heading text-lg text-forest mb-4 flex items-center gap-2">
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Na terenie</span>
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {attractions.filter(a => a.type === 'onsite').map((att) => (
                        <AttractionCard 
                          key={att.id} 
                          attraction={att} 
                          onEdit={() => setEditAttraction(att)}
                          onDelete={() => handleDeleteAttraction(att.id)}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Nearby attractions */}
                  <div>
                    <h3 className="font-heading text-lg text-forest mb-4 flex items-center gap-2">
                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">W pobliżu</span>
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {attractions.filter(a => a.type === 'nearby' || !a.type).map((att) => (
                        <AttractionCard 
                          key={att.id} 
                          attraction={att} 
                          onEdit={() => setEditAttraction(att)}
                          onDelete={() => handleDeleteAttraction(att.id)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Amenities Management */}
          <TabsContent value="amenities">
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-xl text-forest">Zarządzanie udogodnieniami</CardTitle>
                    <CardDescription>Lista udogodnień na terenie obiektu</CardDescription>
                  </div>
                  <Button onClick={() => setShowNewAmenity(true)} className="bg-terracotta hover:bg-terracotta-dark gap-2">
                    <Plus className="w-4 h-4" /> Dodaj udogodnienie
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Free amenities */}
                  <div>
                    <h3 className="font-heading text-lg text-forest mb-4">
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Bezpłatne</span>
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {amenities.filter(a => a.free !== false).map((amenity) => (
                        <AmenityCard 
                          key={amenity.id} 
                          amenity={amenity} 
                          onEdit={() => setEditAmenity(amenity)}
                          onDelete={() => handleDeleteAmenity(amenity.id)}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Paid amenities */}
                  <div>
                    <h3 className="font-heading text-lg text-forest mb-4">
                      <span className="bg-terracotta/20 text-terracotta px-3 py-1 rounded-full text-sm">Płatne</span>
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {amenities.filter(a => a.free === false).map((amenity) => (
                        <AmenityCard 
                          key={amenity.id} 
                          amenity={amenity} 
                          onEdit={() => setEditAmenity(amenity)}
                          onDelete={() => handleDeleteAmenity(amenity.id)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Features Management */}
          <TabsContent value="features">
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-xl text-forest">Zarządzanie atutami</CardTitle>
                    <CardDescription>Atuty wyświetlane pod sekcją Hero</CardDescription>
                  </div>
                  <Button onClick={() => setShowNewFeature(true)} className="bg-terracotta hover:bg-terracotta-dark gap-2">
                    <Plus className="w-4 h-4" /> Dodaj atut
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {features.map((feat) => (
                    <div key={feat.id} className="bg-white p-4 rounded shadow-soft flex items-center gap-3">
                      <div className="w-12 h-12 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Star className="w-5 h-5 text-terracotta" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-body text-forest font-medium truncate">{feat.text}</h4>
                        <p className="font-body text-xs text-forest/50">Ikona: {feat.icon}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditFeature(feat)} className="h-8 w-8 p-0">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteFeature(feat.id)} className="h-8 w-8 p-0 text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gallery Management */}
          <TabsContent value="gallery">
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="font-heading text-xl text-forest">Zarządzanie galerią</CardTitle>
                    <CardDescription>Zdjęcia wyświetlane w sekcji Galeria</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowNewCategory(true)} className="gap-2">
                      <FolderPlus className="w-4 h-4" /> Dodaj kategorię
                    </Button>
                    <Button onClick={() => setShowNewGalleryImage(true)} className="bg-terracotta hover:bg-terracotta-dark gap-2">
                      <Plus className="w-4 h-4" /> Dodaj zdjęcie
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Categories */}
                <div className="mb-6 p-4 bg-sand/50 rounded-lg">
                  <h4 className="font-heading text-sm text-forest mb-3">Kategorie:</h4>
                  <div className="flex flex-wrap gap-2">
                    {galleryCategories.map(cat => (
                      <div key={cat.id} className="flex items-center gap-1 bg-white px-3 py-1.5 rounded-full shadow-sm">
                        <span className="font-body text-sm text-forest">{cat.label}</span>
                        <button onClick={() => handleDeleteCategory(cat.id)} className="text-red-400 hover:text-red-600 ml-1">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {galleryCategories.length === 0 && (
                      <span className="text-sm text-forest/50">Brak kategorii - dodaj pierwszą</span>
                    )}
                  </div>
                </div>
                
                {/* Add Category Form */}
                {showNewCategory && (
                  <div className="mb-6 p-4 bg-white rounded-lg border-2 border-dashed border-terracotta/30">
                    <h4 className="font-heading text-sm text-forest mb-3">Nowa kategoria:</h4>
                    <div className="flex gap-2 flex-wrap">
                      <Input 
                        placeholder="Nazwa (np. krajobraz)" 
                        value={newCategoryName} 
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        className="w-40"
                      />
                      <Input 
                        placeholder="Etykieta (np. Krajobrazy)" 
                        value={newCategoryLabel} 
                        onChange={(e) => setNewCategoryLabel(e.target.value)}
                        className="w-40"
                      />
                      <Button onClick={handleAddCategory} className="bg-forest">Dodaj</Button>
                      <Button variant="ghost" onClick={() => setShowNewCategory(false)}>Anuluj</Button>
                    </div>
                  </div>
                )}
                
                {/* Gallery Grid */}
                <p className="text-xs text-forest/60 mb-3">Najedź na zdjęcie, aby zmienić kolejność lub edytować</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {gallery.map((img, index) => (
                    <div key={img.id} className="relative group">
                      <img src={img.src} alt={img.title || 'Zdjęcie'} className="w-full h-32 object-cover rounded" />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 rounded">
                        {index > 0 && (
                          <Button size="sm" variant="secondary" onClick={() => moveGalleryImage(index, index - 1)} className="h-8 w-8 p-0" title="Przesuń w lewo">
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="secondary" onClick={() => setEditGalleryImage(img)} className="h-8 w-8 p-0" title="Edytuj">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteGalleryImage(img.id)} className="h-8 w-8 p-0" title="Usuń">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        {index < gallery.length - 1 && (
                          <Button size="sm" variant="secondary" onClick={() => moveGalleryImage(index, index + 1)} className="h-8 w-8 p-0" title="Przesuń w prawo">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="absolute top-1 left-1 bg-forest/80 text-white text-xs px-1.5 py-0.5 rounded">
                        {index + 1}
                      </div>
                      {img.show_on_homepage && (
                        <div className="absolute top-1 right-1 bg-green-500 text-white p-1 rounded-full" title="Na stronie głównej">
                          <Check className="w-3 h-3" />
                        </div>
                      )}
                      {img.title && <p className="font-body text-sm text-forest mt-1 truncate">{img.title}</p>}
                      {img.category && (
                        <span className="text-xs bg-sand px-2 py-0.5 rounded text-forest/60">
                          {galleryCategories.find(c => c.name === img.category)?.label || img.category}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Change Password */}
              <Card className="border-0 shadow-soft">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Key className="w-5 h-5" /> Zmiana hasła
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <Label>Aktualne hasło</Label>
                      <Input type="password" value={passwordData.current_password} onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })} required />
                    </div>
                    <div>
                      <Label>Nowe hasło</Label>
                      <Input type="password" value={passwordData.new_password} onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })} required minLength={6} />
                    </div>
                    <div>
                      <Label>Potwierdź nowe hasło</Label>
                      <Input type="password" value={passwordData.confirm_password} onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })} required />
                    </div>
                    <Button type="submit" className="bg-forest hover:bg-forest-light">Zmień hasło</Button>
                  </form>
                </CardContent>
              </Card>

              {/* 2FA Settings */}
              <Card className="border-0 shadow-soft">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5" /> Uwierzytelnianie dwuskładnikowe (2FA)
                  </CardTitle>
                  <CardDescription>Google Authenticator</CardDescription>
                </CardHeader>
                <CardContent>
                  {twoFAStatus.enabled ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-green-600">
                        <Check className="w-5 h-5" />
                        <span className="font-body font-semibold">2FA jest włączone</span>
                      </div>
                      <Button variant="destructive" onClick={handleDisable2FA}>Wyłącz 2FA</Button>
                    </div>
                  ) : twoFASetup ? (
                    <div className="space-y-4">
                      <p className="text-sm text-forest/70">Zeskanuj kod QR w aplikacji Google Authenticator:</p>
                      <div className="flex justify-center bg-white p-4 rounded">
                        <QRCodeSVG value={twoFASetup.provisioning_uri} size={180} />
                      </div>
                      <p className="text-xs text-forest/50 text-center">Lub wprowadź ręcznie: <code className="bg-sand px-2 py-1 rounded">{twoFASetup.secret}</code></p>
                      <div>
                        <Label>Wprowadź kod weryfikacyjny</Label>
                        <div className="flex gap-2 mt-1">
                          <Input id="totp-verify" maxLength={6} placeholder="000000" className="font-mono text-center" />
                          <Button onClick={() => handleEnable2FA(document.getElementById('totp-verify').value)} className="bg-forest hover:bg-forest-light">Włącz</Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-forest/70">Zabezpiecz konto dodatkowym kodem z aplikacji Google Authenticator.</p>
                      <Button onClick={handleSetup2FA} className="bg-forest hover:bg-forest-light">Skonfiguruj 2FA</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Admin Users */}
            <Card className="border-0 shadow-soft">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Users className="w-5 h-5" /> Użytkownicy panelu
                  </CardTitle>
                  <CardDescription>Zarządzaj kontami administratorów</CardDescription>
                </div>
                <Button onClick={() => setShowNewUser(true)} className="bg-forest hover:bg-forest-light gap-2">
                  <UserPlus className="w-4 h-4" /> Dodaj użytkownika
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {adminUsers.map(user => (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-sand/50 rounded">
                      <div>
                        <p className="font-body font-semibold text-forest">{user.username}</p>
                        <p className="text-sm text-forest/60">{user.email}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(user.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* New User Dialog */}
                <Dialog open={showNewUser} onOpenChange={setShowNewUser}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Nowy użytkownik</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4">
                      <div>
                        <Label>Nazwa użytkownika</Label>
                        <Input value={newUserData.username} onChange={(e) => setNewUserData({ ...newUserData, username: e.target.value })} required />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input type="email" value={newUserData.email} onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })} required />
                      </div>
                      <div>
                        <Label>Hasło</Label>
                        <Input type="password" value={newUserData.password} onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })} required minLength={6} />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setShowNewUser(false)}>Anuluj</Button>
                        <Button type="submit" className="bg-forest hover:bg-forest-light">Utwórz</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            {/* Menu Management */}
            <Card className="border-0 shadow-soft">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Menu className="w-5 h-5" /> Kolejność menu
                  </CardTitle>
                  <CardDescription>Zmień kolejność elementów w menu strony</CardDescription>
                </div>
                <Button onClick={handleSaveMenuItems} className="bg-forest hover:bg-forest-light gap-2">
                  <Save className="w-4 h-4" /> Zapisz zmiany
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {menuItems.map((item, index) => (
                    <div key={item.id} className="flex items-center gap-3 p-3 bg-sand/50 rounded">
                      <div className="flex flex-col gap-1">
                        <Button variant="ghost" size="sm" onClick={() => moveMenuItem(index, 'up')} disabled={index === 0} className="h-6 w-6 p-0">
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => moveMenuItem(index, 'down')} disabled={index === menuItems.length - 1} className="h-6 w-6 p-0">
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                      </div>
                      <span className="w-6 text-center font-mono text-forest/50">{index + 1}</span>
                      <div className="flex-1">
                        <Input value={item.name} onChange={(e) => { const newItems = [...menuItems]; newItems[index].name = e.target.value; setMenuItems(newItems); }} className="font-body" />
                      </div>
                      <div className="flex-1">
                        <Input value={item.href} onChange={(e) => { const newItems = [...menuItems]; newItems[index].href = e.target.value; setMenuItems(newItems); }} className="font-mono text-sm text-forest/60" placeholder="Link" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={item.is_route} onCheckedChange={(checked) => { const newItems = [...menuItems]; newItems[index].is_route = checked; setMenuItems(newItems); }} />
                        <Label className="text-xs">Podstrona</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={item.active !== false} onCheckedChange={(checked) => { const newItems = [...menuItems]; newItems[index].active = checked; setMenuItems(newItems); }} />
                        <Label className="text-xs">{item.active !== false ? 'Widoczny' : 'Ukryty'}</Label>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Social Links Management */}
            <Card className="border-0 shadow-soft">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <LucideIcons.Share2 className="w-5 h-5" /> Linki Social Media
                  </CardTitle>
                  <CardDescription>Zarządzaj linkami do mediów społecznościowych</CardDescription>
                </div>
                <Button onClick={handleSaveSocialLinks} className="bg-forest hover:bg-forest-light gap-2">
                  <Save className="w-4 h-4" /> Zapisz zmiany
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {socialLinks.map((link) => (
                    <div key={link.id} className="flex items-center gap-3 p-3 bg-sand/50 rounded">
                      <div className="w-24 flex items-center gap-2">
                        {link.name === 'facebook' && <LucideIcons.Facebook className="w-5 h-5 text-blue-600" />}
                        {link.name === 'instagram' && <LucideIcons.Instagram className="w-5 h-5 text-pink-600" />}
                        {link.name === 'tiktok' && <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>}
                        {link.name === 'google' && <LucideIcons.Globe className="w-5 h-5 text-red-500" />}
                        {link.name === 'booking' && <LucideIcons.ExternalLink className="w-5 h-5 text-blue-800" />}
                        <span className="capitalize font-body text-sm">{link.name}</span>
                      </div>
                      <div className="flex-1">
                        <Input 
                          value={link.url} 
                          onChange={(e) => updateSocialLink(link.id, 'url', e.target.value)} 
                          className="font-mono text-sm" 
                          placeholder={`Link do ${link.name}`}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={link.enabled} 
                          onCheckedChange={(checked) => updateSocialLink(link.id, 'enabled', checked)} 
                        />
                        <Label className="text-xs">{link.enabled ? 'Aktywny' : 'Wyłączony'}</Label>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* SMTP Configuration */}
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <LucideIcons.Mail className="w-5 h-5" /> Konfiguracja SMTP
                </CardTitle>
                <CardDescription>Ustawienia serwera email do wysyłania powiadomień</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveSmtp} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Serwer SMTP</Label>
                      <Input value={smtpConfig.host} onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })} placeholder="smtp.gmail.com" required />
                    </div>
                    <div>
                      <Label>Port</Label>
                      <Input type="number" value={smtpConfig.port} onChange={(e) => setSmtpConfig({ ...smtpConfig, port: parseInt(e.target.value) })} placeholder="587" required />
                    </div>
                    <div>
                      <Label>Użytkownik (login)</Label>
                      <Input value={smtpConfig.username} onChange={(e) => setSmtpConfig({ ...smtpConfig, username: e.target.value })} placeholder="user@gmail.com" required />
                    </div>
                    <div>
                      <Label>Hasło</Label>
                      <Input type="password" value={smtpConfig.password} onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })} placeholder="••••••••" required />
                    </div>
                    <div>
                      <Label>Email nadawcy</Label>
                      <Input type="email" value={smtpConfig.from_email} onChange={(e) => setSmtpConfig({ ...smtpConfig, from_email: e.target.value })} placeholder="noreply@agromiedzierza.com" required />
                    </div>
                    <div>
                      <Label>Nazwa nadawcy</Label>
                      <Input value={smtpConfig.from_name} onChange={(e) => setSmtpConfig({ ...smtpConfig, from_name: e.target.value })} placeholder="Agromiedzierza CMS" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={smtpConfig.use_tls} onCheckedChange={(checked) => setSmtpConfig({ ...smtpConfig, use_tls: checked })} />
                    <Label>Użyj TLS (STARTTLS)</Label>
                  </div>
                  <Button type="submit" disabled={isSavingSmtp} className="bg-forest hover:bg-forest-light">
                    {isSavingSmtp ? 'Zapisywanie...' : 'Zapisz konfigurację'}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t">
                  <Label className="mb-2 block">Test połączenia</Label>
                  <div className="flex gap-2">
                    <Input value={smtpTestEmail} onChange={(e) => setSmtpTestEmail(e.target.value)} placeholder="twoj@email.pl" type="email" className="max-w-xs" />
                    <Button type="button" variant="outline" onClick={handleTestSmtp}>Wyślij email testowy</Button>
                  </div>
                  <p className="text-xs text-forest/50 mt-2">Wysyła email testowy na podany adres, aby sprawdzić poprawność konfiguracji.</p>
                </div>
              </CardContent>
            </Card>

            {/* Google Reviews */}
            <Card className="border-0 shadow-soft">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Star className="w-5 h-5" /> Opinie Google
                  </CardTitle>
                  <CardDescription>Zarządzaj opiniami wyświetlanymi na stronie</CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch checked={reviewsConfig.enabled} onCheckedChange={handleToggleReviews} />
                    <Label>{reviewsConfig.enabled ? 'Włączone' : 'Wyłączone'}</Label>
                  </div>
                  <Button onClick={() => setShowNewReview(true)} className="bg-forest hover:bg-forest-light gap-2">
                    <Plus className="w-4 h-4" /> Dodaj opinię
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Rating & Reviews Count Settings */}
                <div className="grid md:grid-cols-2 gap-4 mb-6 p-4 bg-sand/30 rounded-lg">
                  <div>
                    <Label>Średnia ocena (wyświetlana na stronie)</Label>
                    <Input 
                      type="number" 
                      step="0.1" 
                      min="1" 
                      max="5" 
                      value={reviewsConfig.average_rating || 5.0} 
                      onChange={(e) => setReviewsConfig({ ...reviewsConfig, average_rating: parseFloat(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Liczba opinii (wyświetlana na stronie)</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      value={reviewsConfig.total_reviews || 0} 
                      onChange={(e) => setReviewsConfig({ ...reviewsConfig, total_reviews: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Button onClick={() => handleToggleReviews(reviewsConfig.enabled)} variant="outline" size="sm">
                      <Save className="w-4 h-4 mr-2" /> Zapisz ustawienia oceny
                    </Button>
                  </div>
                </div>

                {/* Reviews List */}
                <div className="space-y-3">
                  {reviews.map(review => (
                    <div key={review.id} className="flex items-start gap-4 p-4 bg-sand/50 rounded">
                      {review.profile_photo_url ? (
                        <img src={review.profile_photo_url} alt={review.author_name} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 bg-forest/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Users className="w-6 h-6 text-forest" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-body font-semibold text-forest">{review.author_name}</span>
                          <div className="flex">
                            {Array.from({ length: 5 }, (_, i) => (
                              <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-forest/70">{review.text}</p>
                        <p className="text-xs text-forest/50 mt-1">
                          {new Date(review.time).toLocaleDateString('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteReview(review.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {reviews.length === 0 && (
                    <p className="text-center text-forest/50 py-4">Brak opinii. Dodaj pierwszą!</p>
                  )}
                </div>

                {/* Add Review Dialog */}
                <Dialog open={showNewReview} onOpenChange={setShowNewReview}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Dodaj opinię</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddReview} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <Label>Imię i nazwisko</Label>
                          <Input value={newReview.author_name} onChange={(e) => setNewReview({ ...newReview, author_name: e.target.value })} placeholder="Jan Kowalski" required />
                        </div>
                        <div>
                          <Label>Data opinii</Label>
                          <Input 
                            type="date" 
                            value={newReview.time ? newReview.time.split('T')[0] : new Date().toISOString().split('T')[0]} 
                            onChange={(e) => setNewReview({ ...newReview, time: e.target.value + 'T12:00:00Z' })}
                          />
                        </div>
                        <div>
                          <Label>URL zdjęcia (opcjonalnie)</Label>
                          <Input 
                            value={newReview.profile_photo_url || ''} 
                            onChange={(e) => setNewReview({ ...newReview, profile_photo_url: e.target.value })} 
                            placeholder="https://..."
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Ocena</Label>
                        <div className="flex gap-2 mt-2">
                          {[1, 2, 3, 4, 5].map(rating => (
                            <button
                              key={rating}
                              type="button"
                              onClick={() => setNewReview({ ...newReview, rating })}
                              className="p-1"
                            >
                              <Star className={`w-8 h-8 ${rating <= newReview.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label>Treść opinii</Label>
                        <Textarea value={newReview.text} onChange={(e) => setNewReview({ ...newReview, text: e.target.value })} placeholder="Świetne miejsce..." rows={4} required />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setShowNewReview(false)}>Anuluj</Button>
                        <Button type="submit" className="bg-forest hover:bg-forest-light">Dodaj</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            {/* Promo Popup */}
            <Card className="border-0 shadow-soft">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <LucideIcons.Gift className="w-5 h-5" /> Popup Promocyjny
                  </CardTitle>
                  <CardDescription>Wyskakujące okienko z ofertą specjalną dla odwiedzających</CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={promoConfig.enabled} 
                      onCheckedChange={(checked) => setPromoConfig({ ...promoConfig, enabled: checked })} 
                    />
                    <Label>{promoConfig.enabled ? 'Włączony' : 'Wyłączony'}</Label>
                  </div>
                  <Button onClick={handleSavePromoConfig} className="bg-forest hover:bg-forest-light gap-2">
                    <Save className="w-4 h-4" /> Zapisz
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Tytuł</Label>
                    <Input 
                      value={promoConfig.title || ''} 
                      onChange={(e) => setPromoConfig({ ...promoConfig, title: e.target.value })}
                      placeholder="🎁 Specjalna oferta!"
                    />
                  </div>
                  <div>
                    <Label>Podtytuł</Label>
                    <Input 
                      value={promoConfig.subtitle || ''} 
                      onChange={(e) => setPromoConfig({ ...promoConfig, subtitle: e.target.value })}
                      placeholder="Tylko dla nowych gości"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Opis oferty</Label>
                    <Textarea 
                      value={promoConfig.description || ''} 
                      onChange={(e) => setPromoConfig({ ...promoConfig, description: e.target.value })}
                      placeholder="Zarezerwuj pobyt teraz i otrzymaj 10% zniżki!"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Tekst przycisku</Label>
                    <Input 
                      value={promoConfig.button_text || ''} 
                      onChange={(e) => setPromoConfig({ ...promoConfig, button_text: e.target.value })}
                      placeholder="Skorzystaj z oferty"
                    />
                  </div>
                  <div>
                    <Label>Link przycisku</Label>
                    <Input 
                      value={promoConfig.button_link || ''} 
                      onChange={(e) => setPromoConfig({ ...promoConfig, button_link: e.target.value })}
                      placeholder="#contact"
                    />
                  </div>
                  <div>
                    <Label>Kod rabatowy (opcjonalnie)</Label>
                    <Input 
                      value={promoConfig.discount_code || ''} 
                      onChange={(e) => setPromoConfig({ ...promoConfig, discount_code: e.target.value })}
                      placeholder="LATO2025"
                    />
                  </div>
                  <div>
                    <Label>Kolor tła</Label>
                    <div className="flex gap-2">
                      <Input 
                        type="color"
                        value={promoConfig.background_color || '#1A3C34'} 
                        onChange={(e) => setPromoConfig({ ...promoConfig, background_color: e.target.value })}
                        className="w-16 h-10 p-1"
                      />
                      <Input 
                        value={promoConfig.background_color || '#1A3C34'} 
                        onChange={(e) => setPromoConfig({ ...promoConfig, background_color: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Pokaż po (sekundy)</Label>
                    <Input 
                      type="number"
                      min="1"
                      value={promoConfig.show_after_seconds || 5} 
                      onChange={(e) => setPromoConfig({ ...promoConfig, show_after_seconds: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={promoConfig.show_once_per_session} 
                      onCheckedChange={(checked) => setPromoConfig({ ...promoConfig, show_once_per_session: checked })} 
                    />
                    <Label>Pokaż tylko raz na sesję</Label>
                  </div>
                </div>

                {/* Preview */}
                {promoConfig.enabled && (
                  <div className="mt-6 p-4 bg-sand/50 rounded-lg">
                    <p className="text-sm text-forest/60 mb-2">Podgląd:</p>
                    <div 
                      className="p-4 rounded-lg text-white text-center max-w-xs mx-auto"
                      style={{ backgroundColor: promoConfig.background_color || '#1A3C34' }}
                    >
                      <p className="font-heading text-lg">{promoConfig.title || '🎁 Specjalna oferta!'}</p>
                      <p className="text-sm opacity-80">{promoConfig.subtitle || 'Tylko dla nowych gości'}</p>
                      <p className="text-sm mt-2">{promoConfig.description || 'Opis oferty...'}</p>
                      {promoConfig.discount_code && (
                        <p className="font-mono mt-2 bg-white/20 px-2 py-1 rounded inline-block">{promoConfig.discount_code}</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SEO Configuration */}
            <Card className="border-0 shadow-soft">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <LucideIcons.Search className="w-5 h-5" /> SEO i Meta Tagi
                  </CardTitle>
                  <CardDescription>Optymalizacja dla wyszukiwarek i mediów społecznościowych</CardDescription>
                </div>
                <Button onClick={handleSaveSeoConfig} className="bg-forest hover:bg-forest-light gap-2">
                  <Save className="w-4 h-4" /> Zapisz SEO
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Meta Tags */}
                <div className="space-y-4">
                  <h4 className="font-heading text-lg text-forest">Podstawowe meta tagi</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label>Tytuł strony (meta title)</Label>
                      <Input 
                        value={seoConfig.meta_title || ''} 
                        onChange={(e) => setSeoConfig({ ...seoConfig, meta_title: e.target.value })}
                        placeholder="VIP Village Agromiedzierza - Agroturystyka w Świętokrzyskim"
                      />
                      <p className="text-xs text-forest/60 mt-1">Zalecana długość: 50-60 znaków ({(seoConfig.meta_title || '').length} znaków)</p>
                    </div>
                    <div className="md:col-span-2">
                      <Label>Opis strony (meta description)</Label>
                      <Textarea 
                        value={seoConfig.meta_description || ''} 
                        onChange={(e) => setSeoConfig({ ...seoConfig, meta_description: e.target.value })}
                        placeholder="Luksusowy wypoczynek w sercu natury..."
                        rows={2}
                      />
                      <p className="text-xs text-forest/60 mt-1">Zalecana długość: 150-160 znaków ({(seoConfig.meta_description || '').length} znaków)</p>
                    </div>
                    <div className="md:col-span-2">
                      <Label>Słowa kluczowe (meta keywords)</Label>
                      <Input 
                        value={seoConfig.meta_keywords || ''} 
                        onChange={(e) => setSeoConfig({ ...seoConfig, meta_keywords: e.target.value })}
                        placeholder="agroturystyka, świętokrzyskie, domki, nocleg"
                      />
                    </div>
                  </div>
                </div>

                {/* Open Graph */}
                <div className="space-y-4">
                  <h4 className="font-heading text-lg text-forest">Open Graph (Facebook, LinkedIn)</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>OG Tytuł (opcjonalnie)</Label>
                      <Input 
                        value={seoConfig.og_title || ''} 
                        onChange={(e) => setSeoConfig({ ...seoConfig, og_title: e.target.value })}
                        placeholder="Użyje meta title jeśli puste"
                      />
                    </div>
                    <div>
                      <Label>OG Obrazek (URL)</Label>
                      <Input 
                        value={seoConfig.og_image || ''} 
                        onChange={(e) => setSeoConfig({ ...seoConfig, og_image: e.target.value })}
                        placeholder="https://example.com/og-image.jpg"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>OG Opis (opcjonalnie)</Label>
                      <Textarea 
                        value={seoConfig.og_description || ''} 
                        onChange={(e) => setSeoConfig({ ...seoConfig, og_description: e.target.value })}
                        placeholder="Użyje meta description jeśli puste"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>

                {/* Technical SEO */}
                <div className="space-y-4">
                  <h4 className="font-heading text-lg text-forest">Ustawienia techniczne</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Canonical URL</Label>
                      <Input 
                        value={seoConfig.canonical_url || ''} 
                        onChange={(e) => setSeoConfig({ ...seoConfig, canonical_url: e.target.value })}
                        placeholder="https://agromiedzierza.com"
                      />
                    </div>
                    <div>
                      <Label>Robots</Label>
                      <Select value={seoConfig.robots || 'index, follow'} onValueChange={(v) => setSeoConfig({ ...seoConfig, robots: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="index, follow">index, follow (zalecane)</SelectItem>
                          <SelectItem value="noindex, follow">noindex, follow</SelectItem>
                          <SelectItem value="index, nofollow">index, nofollow</SelectItem>
                          <SelectItem value="noindex, nofollow">noindex, nofollow</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label>Google Site Verification</Label>
                      <Input 
                        value={seoConfig.google_site_verification || ''} 
                        onChange={(e) => setSeoConfig({ ...seoConfig, google_site_verification: e.target.value })}
                        placeholder="Kod weryfikacji Google Search Console"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Image Alt Texts */}
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <LucideIcons.Image className="w-5 h-5" /> Teksty alternatywne zdjęć (Alt)
                </CardTitle>
                <CardDescription>Opisy zdjęć dla SEO i dostępności</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { key: 'hero_image', label: 'Zdjęcie główne (Hero)' },
                    { key: 'about_image1', label: 'O nas - Zdjęcie 1' },
                    { key: 'about_image2', label: 'O nas - Zdjęcie 2' },
                    { key: 'balia_image', label: 'Balia' },
                    { key: 'sauna_image', label: 'Sauna' },
                    { key: 'basen_image', label: 'Basen' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-4">
                      <Label className="w-40 shrink-0">{label}</Label>
                      <Input 
                        value={imageAltTexts[key] || ''} 
                        onChange={(e) => setImageAltTexts({ ...imageAltTexts, [key]: e.target.value })}
                        placeholder={`Opis zdjęcia: ${label.toLowerCase()}`}
                        className="flex-1"
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleSaveImageAltText(key, imageAltTexts[key] || '')}
                      >
                        Zapisz
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Accommodation Edit Dialog */}
      <AccommodationEditDialog 
        accommodation={editAccommodation || (showNewAccommodation ? {} : null)}
        isNew={showNewAccommodation}
        onClose={() => { setEditAccommodation(null); setShowNewAccommodation(false); }}
        onSave={handleSaveAccommodation}
        getAuthHeaders={getAuthHeaders}
      />

      {/* Attraction Edit Dialog */}
      <AttractionEditDialog 
        attraction={editAttraction || (showNewAttraction ? {} : null)}
        isNew={showNewAttraction}
        onClose={() => { setEditAttraction(null); setShowNewAttraction(false); }}
        onSave={handleSaveAttraction}
        getAuthHeaders={getAuthHeaders}
      />

      {/* Feature Edit Dialog */}
      <FeatureEditDialog 
        feature={editFeature || (showNewFeature ? {} : null)}
        isNew={showNewFeature}
        onClose={() => { setEditFeature(null); setShowNewFeature(false); }}
        onSave={handleSaveFeature}
        availableIcons={availableIcons}
      />

      {/* Amenity Edit Dialog */}
      <AmenityEditDialog 
        amenity={editAmenity || (showNewAmenity ? {} : null)}
        isNew={showNewAmenity}
        onClose={() => { setEditAmenity(null); setShowNewAmenity(false); }}
        onSave={handleSaveAmenity}
        availableIcons={availableIcons}
      />

      {/* Gallery Image Edit Dialog */}
      <GalleryImageEditDialog 
        image={editGalleryImage || (showNewGalleryImage ? {} : null)}
        isNew={showNewGalleryImage}
        onClose={() => { setEditGalleryImage(null); setShowNewGalleryImage(false); }}
        onSave={handleSaveGalleryImage}
        galleryCategories={galleryCategories}
      />
    </div>
  );
};

// Content Section Component
const ContentSection = ({ title, description, icon, children }) => (
  <Card className="border-0 shadow-soft">
    <CardHeader>
      <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
        {icon}
        {title}
      </CardTitle>
      {description && <CardDescription>{description}</CardDescription>}
    </CardHeader>
    <CardContent className="space-y-4">
      {children}
    </CardContent>
  </Card>
);

// Rich Text Editor Field Component
const WysiwygField = ({ label, fieldKey, value, onSave }) => {
  const [editValue, setEditValue] = useState(value || '');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  const handleSave = () => {
    onSave(fieldKey, editValue);
    setIsEditing(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-forest/70">{label}</Label>
        {!isEditing ? (
          <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="h-7 px-2 text-terracotta">
            <Edit className="w-3 h-3 mr-1" /> Edytuj
          </Button>
        ) : (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={handleSave} className="h-7 px-2 text-green-600">
              <Save className="w-3 h-3 mr-1" /> Zapisz
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setEditValue(value || ''); setIsEditing(false); }} className="h-7 px-2 text-red-500">
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
      
      {isEditing ? (
        <RichTextEditor content={editValue} onChange={setEditValue} />
      ) : (
        <div className="bg-sand/50 p-4 rounded font-body text-sm text-forest prose prose-sm max-w-none" 
             dangerouslySetInnerHTML={{ __html: value || '<em class="text-forest/40">Brak treści - kliknij Edytuj aby dodać</em>' }} />
      )}
    </div>
  );
};

// Content Field with Upload Component
const ContentFieldWithUpload = ({ label, fieldKey, value, onSave, type = "text", multiline = false, rows = 3, getAuthHeaders }) => {
  const [editValue, setEditValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    onSave(fieldKey, editValue);
    setIsEditing(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setEditValue(response.data.url);
      toast.success('Zdjęcie przesłane');
    } catch (error) {
      toast.error('Błąd przesyłania zdjęcia');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-forest/70">{label}</Label>
        {!isEditing ? (
          <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="h-7 px-2 text-terracotta">
            <Edit className="w-3 h-3 mr-1" /> Edytuj
          </Button>
        ) : (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={handleSave} className="h-7 px-2 text-green-600">
              <Save className="w-3 h-3 mr-1" /> Zapisz
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setEditValue(value); setIsEditing(false); }} className="h-7 px-2 text-red-500">
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
      
      {isEditing ? (
        <>
          {type === 'image' ? (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input 
                  value={editValue} 
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="URL zdjęcia lub prześlij plik"
                  className="font-body flex-1"
                />
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="gap-2">
                  <Upload className="w-4 h-4" />
                  {isUploading ? 'Wysyłanie...' : 'Prześlij'}
                </Button>
              </div>
              {editValue && <img src={editValue} alt="Podgląd" className="mt-2 max-h-40 object-cover rounded" />}
            </div>
          ) : multiline ? (
            <Textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} rows={rows} className="font-body" />
          ) : (
            <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} className="font-body" />
          )}
        </>
      ) : (
        <div className="bg-sand/50 p-3 rounded font-body text-sm text-forest">
          {type === 'image' && value ? (
            <div className="flex items-center gap-3">
              <img src={value} alt="Podgląd" className="h-16 w-24 object-cover rounded" />
              <span className="text-forest/60 truncate flex-1">{value}</span>
            </div>
          ) : (
            <span className={multiline ? 'whitespace-pre-wrap' : 'truncate block'}>
              {value || <span className="text-forest/40 italic">Brak treści</span>}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Content Field Component
const ContentField = ({ label, fieldKey, value, onSave, type = "text", multiline = false, rows = 3 }) => {
  const [editValue, setEditValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    onSave(fieldKey, editValue);
    setIsEditing(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-forest/70">{label}</Label>
        {!isEditing ? (
          <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="h-7 px-2 text-terracotta">
            <Edit className="w-3 h-3 mr-1" /> Edytuj
          </Button>
        ) : (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={handleSave} className="h-7 px-2 text-green-600">
              <Save className="w-3 h-3 mr-1" /> Zapisz
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setEditValue(value); setIsEditing(false); }} className="h-7 px-2 text-red-500">
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
      
      {isEditing ? (
        <>
          {multiline ? (
            <Textarea 
              value={editValue} 
              onChange={(e) => setEditValue(e.target.value)}
              rows={rows}
              className="font-body"
            />
          ) : (
            <Input 
              value={editValue} 
              onChange={(e) => setEditValue(e.target.value)}
              className="font-body"
            />
          )}
          {type === 'image' && editValue && (
            <img src={editValue} alt="Podgląd" className="mt-2 max-h-32 object-cover rounded" />
          )}
        </>
      ) : (
        <div className="bg-sand/50 p-3 rounded font-body text-sm text-forest">
          {type === 'image' && value ? (
            <div className="flex items-center gap-3">
              <img src={value} alt="Podgląd" className="h-16 w-24 object-cover rounded" />
              <span className="text-forest/60 truncate flex-1">{value}</span>
            </div>
          ) : (
            <span className={multiline ? 'whitespace-pre-wrap' : 'truncate block'}>
              {value || <span className="text-forest/40 italic">Brak treści</span>}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Attraction Card Component
const AttractionCard = ({ attraction, onEdit, onDelete }) => (
  <div className="bg-white p-3 rounded shadow-soft">
    <div className="relative h-32 mb-3 overflow-hidden rounded">
      <img src={attraction.image} alt={attraction.name} className="w-full h-full object-cover" />
      <div className="absolute top-2 right-2 flex gap-1">
        <Button size="sm" variant="secondary" onClick={onEdit} className="h-7 w-7 p-0">
          <Edit className="w-3 h-3" />
        </Button>
        <Button size="sm" variant="destructive" onClick={onDelete} className="h-7 w-7 p-0">
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
    <h4 className="font-heading text-forest">{attraction.name}</h4>
    <p className="font-body text-sm text-forest/60">{attraction.distance}</p>
    {attraction.description && (
      <p className="font-body text-xs text-forest/50 mt-1 line-clamp-2">{attraction.description}</p>
    )}
  </div>
);

// Amenity Card Component
const AmenityCard = ({ amenity, onEdit, onDelete }) => (
  <div className="bg-white p-3 rounded shadow-soft flex items-center gap-3">
    <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
      <Sparkles className="w-5 h-5 text-terracotta" />
    </div>
    <div className="flex-1 min-w-0">
      <h4 className="font-body text-forest font-medium text-sm truncate">{amenity.text}</h4>
      {amenity.note && <p className="font-body text-xs text-forest/50">{amenity.note}</p>}
    </div>
    <div className="flex gap-1">
      <Button size="sm" variant="ghost" onClick={onEdit} className="h-7 w-7 p-0">
        <Edit className="w-3 h-3" />
      </Button>
      <Button size="sm" variant="ghost" onClick={onDelete} className="h-7 w-7 p-0 text-red-500">
        <Trash2 className="w-3 h-3" />
      </Button>
    </div>
  </div>
);

// Accommodation Edit Dialog
const AccommodationEditDialog = ({ accommodation, isNew, onClose, onSave, getAuthHeaders }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    capacity: 4,
    price_low_season: 0,
    price_high_season: 0,
    extra_person_price: 50,
    amenities: [],
    images: [],
    active: true,
    included_items: [],
  });
  const [newAmenity, setNewAmenity] = useState('');
  const [newImage, setNewImage] = useState('');
  const [newIncludedItem, setNewIncludedItem] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const defaultIncludedItems = [
    { text: 'Parking gratis', icon: 'car' },
    { text: 'Wi-Fi', icon: 'wifi' },
    { text: 'Pościel', icon: 'bed' },
    { text: 'Altany ogrodowe', icon: 'tree-pine' },
    { text: 'Grille, miejsce na ognisko', icon: 'flame' },
    { text: 'Basen (sezon)', icon: 'waves' },
    { text: 'Tyrolka', icon: 'cable' },
    { text: 'Place zabaw', icon: 'baby' },
    { text: 'Trampolina', icon: 'move' },
    { text: 'Rowery', icon: 'bike' },
  ];

  useEffect(() => {
    if (accommodation && accommodation.id) {
      setFormData({
        id: accommodation.id,
        name: accommodation.name || '',
        description: accommodation.description || '',
        capacity: accommodation.capacity || 4,
        price_low_season: accommodation.price_low_season || 0,
        price_high_season: accommodation.price_high_season || 0,
        extra_person_price: accommodation.extra_person_price || 50,
        amenities: accommodation.amenities || [],
        images: accommodation.images || [],
        active: accommodation.active !== false,
        included_items: accommodation.included_items || defaultIncludedItems,
      });
    } else if (isNew) {
      setFormData({
        name: '',
        description: '',
        capacity: 4,
        price_low_season: 400,
        price_high_season: 450,
        extra_person_price: 50,
        amenities: ['Wi-Fi', 'TV', 'Łazienka'],
        images: [],
        active: true,
        included_items: defaultIncludedItems,
      });
    }
  }, [accommodation, isNew]);

  const addAmenity = () => {
    if (newAmenity.trim()) {
      setFormData({ ...formData, amenities: [...formData.amenities, newAmenity.trim()] });
      setNewAmenity('');
    }
  };

  const removeAmenity = (index) => {
    setFormData({ ...formData, amenities: formData.amenities.filter((_, i) => i !== index) });
  };

  const addIncludedItem = () => {
    if (newIncludedItem.trim()) {
      const newItem = { id: crypto.randomUUID(), text: newIncludedItem.trim(), icon: 'check' };
      setFormData({ ...formData, included_items: [...formData.included_items, newItem] });
      setNewIncludedItem('');
    }
  };

  const removeIncludedItem = (index) => {
    setFormData({ ...formData, included_items: formData.included_items.filter((_, i) => i !== index) });
  };

  const updateIncludedItemText = (index, text) => {
    const updated = [...formData.included_items];
    updated[index] = { ...updated[index], text };
    setFormData({ ...formData, included_items: updated });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, images: [...formData.images, response.data.url] });
      toast.success('Zdjęcie przesłane');
    } catch (error) {
      toast.error('Błąd przesyłania zdjęcia');
    } finally {
      setIsUploading(false);
    }
  };

  const addImage = () => {
    if (newImage.trim() && newImage.startsWith('http')) {
      setFormData({ ...formData, images: [...formData.images, newImage.trim()] });
      setNewImage('');
    } else {
      toast.error('Podaj prawidłowy URL zdjęcia');
    }
  };

  const removeImage = (index) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) });
  };

  const moveImage = (fromIndex, toIndex) => {
    const newImages = [...formData.images];
    const [movedImage] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, movedImage);
    setFormData({ ...formData, images: newImages });
  };

  const setAsMainImage = (index) => {
    const newImages = [...formData.images];
    const [mainImage] = newImages.splice(index, 1);
    newImages.unshift(mainImage);
    setFormData({ ...formData, images: newImages });
    toast.success('Ustawiono jako główne zdjęcie');
  };

  if (!accommodation && !isNew) return null;

  return (
    <Dialog open={!!accommodation || isNew} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest">
            {isNew ? 'Dodaj nowy obiekt' : 'Edytuj obiekt'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Nazwa obiektu *</Label>
              <Input 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="np. Domek letniskowy"
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label>Opis</Label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Pojemność (osób)</Label>
              <Input 
                type="number" 
                value={formData.capacity} 
                onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Dopłata za osobę (zł)</Label>
              <Input 
                type="number" 
                value={formData.extra_person_price} 
                onChange={(e) => setFormData({ ...formData, extra_person_price: parseFloat(e.target.value) })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Cena poza sezonem (zł/noc)</Label>
              <Input 
                type="number" 
                value={formData.price_low_season} 
                onChange={(e) => setFormData({ ...formData, price_low_season: parseFloat(e.target.value) })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Cena w sezonie (zł/noc)</Label>
              <Input 
                type="number" 
                value={formData.price_high_season} 
                onChange={(e) => setFormData({ ...formData, price_high_season: parseFloat(e.target.value) })}
                className="mt-1"
              />
            </div>
          </div>

          {/* Amenities */}
          <div>
            <Label>Udogodnienia obiektu</Label>
            <div className="flex gap-2 mt-2">
              <Input 
                value={newAmenity} 
                onChange={(e) => setNewAmenity(e.target.value)}
                placeholder="np. Wi-Fi, TV, Kuchnia"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity())}
              />
              <Button type="button" onClick={addAmenity} variant="outline">Dodaj</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.amenities.map((amenity, i) => (
                <span key={i} className="bg-sand px-3 py-1 text-sm flex items-center gap-2 rounded">
                  {amenity}
                  <button onClick={() => removeAmenity(i)} className="text-red-500 hover:text-red-700">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Images */}
          <div>
            <Label>Zdjęcia</Label>
            <div className="flex gap-2 mt-2">
              <Input 
                value={newImage} 
                onChange={(e) => setNewImage(e.target.value)}
                placeholder="URL zdjęcia lub prześlij plik"
                className="flex-1"
              />
              <Button type="button" onClick={addImage} variant="outline">Dodaj URL</Button>
              <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="gap-2">
                <Upload className="w-4 h-4" />
                {isUploading ? '...' : 'Prześlij'}
              </Button>
            </div>
            {formData.images.length > 0 && (
              <div className="space-y-2 mt-3">
                <p className="text-xs text-forest/60">Przeciągnij zdjęcia aby zmienić kolejność. Pierwsze zdjęcie = główne (miniaturka)</p>
                <div className="grid grid-cols-2 gap-2">
                  {formData.images.map((img, i) => (
                    <div 
                      key={i} 
                      className={`relative group border-2 rounded overflow-hidden ${i === 0 ? 'border-terracotta ring-2 ring-terracotta/30' : 'border-transparent'}`}
                    >
                      <img src={img} alt="" className="w-full h-20 object-cover" />
                      {i === 0 && (
                        <span className="absolute top-1 left-1 bg-terracotta text-white text-xs px-2 py-0.5 rounded font-medium">
                          Główne
                        </span>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                        {i > 0 && (
                          <button 
                            onClick={() => moveImage(i, i - 1)}
                            className="bg-white text-forest rounded p-1.5 hover:bg-terracotta hover:text-white transition-colors"
                            title="Przesuń w lewo"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                        )}
                        {i === 0 ? null : (
                          <button 
                            onClick={() => setAsMainImage(i)}
                            className="bg-white text-forest rounded p-1.5 hover:bg-terracotta hover:text-white transition-colors"
                            title="Ustaw jako główne"
                          >
                            <Star className="w-4 h-4" />
                          </button>
                        )}
                        {i < formData.images.length - 1 && (
                          <button 
                            onClick={() => moveImage(i, i + 1)}
                            className="bg-white text-forest rounded p-1.5 hover:bg-terracotta hover:text-white transition-colors"
                            title="Przesuń w prawo"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        )}
                        <button 
                          onClick={() => removeImage(i)} 
                          className="bg-red-500 text-white rounded p-1.5 hover:bg-red-600 transition-colors"
                          title="Usuń"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Included Items - W cenie pobytu */}
          <div>
            <Label>W cenie pobytu</Label>
            <p className="text-xs text-forest/60 mb-2">Lista rzeczy zawartych w cenie dla tego obiektu</p>
            <div className="flex gap-2 mt-2">
              <Input 
                value={newIncludedItem} 
                onChange={(e) => setNewIncludedItem(e.target.value)}
                placeholder="np. Parking gratis, Wi-Fi, Pościel"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addIncludedItem())}
              />
              <Button type="button" onClick={addIncludedItem} variant="outline">Dodaj</Button>
            </div>
            <div className="space-y-1 mt-2 max-h-40 overflow-y-auto">
              {formData.included_items.map((item, i) => (
                <div key={item.id || i} className="flex items-center gap-2 bg-sand/50 px-3 py-1 rounded">
                  <span className="w-2 h-2 bg-terracotta rounded-full" />
                  <Input 
                    value={item.text} 
                    onChange={(e) => updateIncludedItemText(i, e.target.value)}
                    className="flex-1 h-7 text-sm border-0 bg-transparent"
                  />
                  <button onClick={() => removeIncludedItem(i)} className="text-red-500 hover:text-red-700">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch 
              checked={formData.active} 
              onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
            />
            <Label>Aktywny (widoczny na stronie)</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anuluj</Button>
          <Button onClick={() => onSave(formData, isNew)} className="bg-forest hover:bg-forest-light">
            {isNew ? 'Dodaj' : 'Zapisz'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Attraction Edit Dialog
const AttractionEditDialog = ({ attraction, isNew, onClose, onSave, getAuthHeaders }) => {
  const [formData, setFormData] = useState({
    name: '',
    distance: '',
    image: '',
    type: 'nearby',
    description: '',
    link: '',
  });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (attraction && attraction.id) {
      setFormData({
        id: attraction.id,
        name: attraction.name || '',
        distance: attraction.distance || '',
        image: attraction.image || '',
        type: attraction.type || 'nearby',
        description: attraction.description || '',
        link: attraction.link || '',
      });
    } else if (isNew) {
      setFormData({ name: '', distance: '', image: '', type: 'nearby', description: '', link: '' });
    }
  }, [attraction, isNew]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, image: response.data.url });
      toast.success('Zdjęcie przesłane');
    } catch (error) {
      toast.error('Błąd przesyłania zdjęcia');
    } finally {
      setIsUploading(false);
    }
  };

  if (!attraction && !isNew) return null;

  return (
    <Dialog open={!!attraction || isNew} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest">
            {isNew ? 'Dodaj atrakcję' : 'Edytuj atrakcję'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label>Nazwa atrakcji</Label>
            <Input 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="np. Jezioro Sielpia"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Typ</Label>
            <Select value={formData.type} onValueChange={(val) => setFormData({ ...formData, type: val })}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="onsite">Na terenie</SelectItem>
                <SelectItem value="nearby">W pobliżu</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Odległość</Label>
            <Input 
              value={formData.distance} 
              onChange={(e) => setFormData({ ...formData, distance: e.target.value })}
              placeholder="np. 3 km lub na terenie"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Opis (opcjonalnie)</Label>
            <Textarea 
              value={formData.description} 
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Krótki opis atrakcji"
              className="mt-1"
              rows={2}
            />
          </div>
          <div>
            <Label>Link (opcjonalnie)</Label>
            <Input 
              value={formData.link} 
              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
              placeholder="np. https://example.com/wiecej"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Zdjęcie</Label>
            <div className="flex gap-2 mt-1">
              <Input 
                value={formData.image} 
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                placeholder="URL zdjęcia lub prześlij plik"
                className="flex-1"
              />
              <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="gap-2">
                <Upload className="w-4 h-4" />
                {isUploading ? 'Wysyłanie...' : 'Prześlij'}
              </Button>
            </div>
            {formData.image && (
              <img src={formData.image} alt="Podgląd" className="mt-2 w-full h-32 object-cover rounded" />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anuluj</Button>
          <Button onClick={() => onSave(formData, isNew)} className="bg-forest hover:bg-forest-light">
            {isNew ? 'Dodaj' : 'Zapisz'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Feature Edit Dialog
const FeatureEditDialog = ({ feature, isNew, onClose, onSave, availableIcons }) => {
  const [formData, setFormData] = useState({ text: '', icon: 'star' });
  const [iconSearch, setIconSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (feature && feature.id) {
      setFormData({ id: feature.id, text: feature.text || '', icon: feature.icon || 'star' });
    } else if (isNew) {
      setFormData({ text: '', icon: 'star' });
    }
  }, [feature, isNew]);

  const filteredIcons = (availableIcons?.icons || []).filter(icon => {
    const matchesSearch = icon.label.toLowerCase().includes(iconSearch.toLowerCase()) || 
                          icon.name.toLowerCase().includes(iconSearch.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || icon.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (!feature && !isNew) return null;

  return (
    <Dialog open={!!feature || isNew} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest">
            {isNew ? 'Dodaj atut' : 'Edytuj atut'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label>Tekst atutu</Label>
            <Input 
              value={formData.text} 
              onChange={(e) => setFormData({ ...formData, text: e.target.value })}
              placeholder="np. Bezpośrednio przy lesie"
              className="mt-1"
            />
          </div>
          
          {/* Icon Selector with Preview */}
          <div>
            <Label>Ikona</Label>
            <div className="mt-2 p-4 bg-sand/50 rounded-lg">
              {/* Current selection */}
              <div className="flex items-center gap-3 mb-4 p-3 bg-white rounded-lg">
                <div className="w-12 h-12 bg-terracotta/10 rounded-full flex items-center justify-center">
                  <IconPreview name={formData.icon} className="w-6 h-6 text-terracotta" />
                </div>
                <div>
                  <p className="font-body font-medium text-forest">Wybrana ikona: {formData.icon}</p>
                  <p className="font-body text-sm text-forest/60">Kliknij poniżej aby zmienić</p>
                </div>
              </div>
              
              {/* Search and filter */}
              <div className="flex gap-2 mb-3">
                <Input 
                  placeholder="Szukaj ikony..." 
                  value={iconSearch}
                  onChange={(e) => setIconSearch(e.target.value)}
                  className="flex-1"
                />
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Kategoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Wszystkie</SelectItem>
                    {(availableIcons?.categories || []).map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Icons grid */}
              <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto">
                {filteredIcons.map(icon => (
                  <button
                    key={icon.name}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: icon.name })}
                    className={`p-2 rounded-lg flex flex-col items-center gap-1 transition hover:bg-terracotta/10 ${
                      formData.icon === icon.name ? 'bg-terracotta/20 ring-2 ring-terracotta' : 'bg-white'
                    }`}
                    title={icon.label}
                  >
                    <IconPreview name={icon.name} className="w-5 h-5 text-forest" />
                    <span className="text-xs text-forest/60 truncate w-full text-center">{icon.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anuluj</Button>
          <Button onClick={() => onSave(formData, isNew)} className="bg-forest hover:bg-forest-light">
            {isNew ? 'Dodaj' : 'Zapisz'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Amenity Edit Dialog
const AmenityEditDialog = ({ amenity, isNew, onClose, onSave, availableIcons }) => {
  const [formData, setFormData] = useState({ text: '', icon: 'sparkles', free: true, note: '' });
  const [iconSearch, setIconSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (amenity && amenity.id) {
      setFormData({ 
        id: amenity.id, 
        text: amenity.text || '', 
        icon: amenity.icon || 'sparkles',
        free: amenity.free !== false,
        note: amenity.note || ''
      });
    } else if (isNew) {
      setFormData({ text: '', icon: 'sparkles', free: true, note: '' });
    }
  }, [amenity, isNew]);

  const filteredIcons = (availableIcons?.icons || []).filter(icon => {
    const matchesSearch = icon.label.toLowerCase().includes(iconSearch.toLowerCase()) || 
                          icon.name.toLowerCase().includes(iconSearch.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || icon.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (!amenity && !isNew) return null;

  return (
    <Dialog open={!!amenity || isNew} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest">
            {isNew ? 'Dodaj udogodnienie' : 'Edytuj udogodnienie'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label>Nazwa udogodnienia</Label>
            <Input 
              value={formData.text} 
              onChange={(e) => setFormData({ ...formData, text: e.target.value })}
              placeholder="np. Basen, Plac zabaw"
              className="mt-1"
            />
          </div>
          
          {/* Icon Selector with Preview */}
          <div>
            <Label>Ikona</Label>
            <div className="mt-2 p-4 bg-sand/50 rounded-lg">
              {/* Current selection */}
              <div className="flex items-center gap-3 mb-4 p-3 bg-white rounded-lg">
                <div className="w-12 h-12 bg-terracotta/10 rounded-full flex items-center justify-center">
                  <IconPreview name={formData.icon} className="w-6 h-6 text-terracotta" />
                </div>
                <div>
                  <p className="font-body font-medium text-forest">Wybrana ikona: {formData.icon}</p>
                  <p className="font-body text-sm text-forest/60">Kliknij poniżej aby zmienić</p>
                </div>
              </div>
              
              {/* Search and filter */}
              <div className="flex gap-2 mb-3">
                <Input 
                  placeholder="Szukaj ikony..." 
                  value={iconSearch}
                  onChange={(e) => setIconSearch(e.target.value)}
                  className="flex-1"
                />
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Kategoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Wszystkie</SelectItem>
                    {(availableIcons?.categories || []).map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Icons grid */}
              <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto">
                {filteredIcons.map(icon => (
                  <button
                    key={icon.name}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: icon.name })}
                    className={`p-2 rounded-lg flex flex-col items-center gap-1 transition hover:bg-terracotta/10 ${
                      formData.icon === icon.name ? 'bg-terracotta/20 ring-2 ring-terracotta' : 'bg-white'
                    }`}
                    title={icon.label}
                  >
                    <IconPreview name={icon.name} className="w-5 h-5 text-forest" />
                    <span className="text-xs text-forest/60 truncate w-full text-center">{icon.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Switch 
              checked={formData.free} 
              onCheckedChange={(checked) => setFormData({ ...formData, free: checked })}
            />
            <Label>Bezpłatne</Label>
          </div>
          <div>
            <Label>Notatka (opcjonalnie)</Label>
            <Input 
              value={formData.note} 
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              placeholder="np. maj-listopad, wakacje"
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anuluj</Button>
          <Button onClick={() => onSave(formData, isNew)} className="bg-forest hover:bg-forest-light">
            {isNew ? 'Dodaj' : 'Zapisz'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Gallery Image/Video Edit Dialog
const GalleryImageEditDialog = ({ image, isNew, onClose, onSave, galleryCategories = [] }) => {
  const [formData, setFormData] = useState({ src: '', title: '', alt_text: '', category: '', media_type: 'image', thumbnail: '', show_on_homepage: true });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (image && image.id) {
      setFormData({ 
        id: image.id, 
        src: image.src || '', 
        title: image.title || '',
        alt_text: image.alt_text || '',
        category: image.category || '',
        media_type: image.media_type || 'image',
        thumbnail: image.thumbnail || '',
        show_on_homepage: image.show_on_homepage !== false
      });
    } else if (isNew) {
      setFormData({ src: '', title: '', alt_text: '', category: '', media_type: 'image', thumbnail: '', show_on_homepage: true });
    }
  }, [image, isNew]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/upload`, fd, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, src: response.data.url });
      toast.success('Zdjęcie przesłane!');
    } catch (error) {
      toast.error('Błąd przesyłania');
    } finally {
      setIsUploading(false);
    }
  };

  if (!image && !isNew) return null;

  const isVideo = formData.media_type === 'video';

  return (
    <Dialog open={!!image || isNew} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest">
            {isNew ? 'Dodaj do galerii' : 'Edytuj element galerii'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Media Type */}
          <div>
            <Label>Typ mediów</Label>
            <div className="flex gap-2 mt-2">
              <Button type="button" variant={!isVideo ? 'default' : 'outline'} onClick={() => setFormData({ ...formData, media_type: 'image' })} className={!isVideo ? 'bg-forest' : ''}>
                <Image className="w-4 h-4 mr-2" /> Zdjęcie
              </Button>
              <Button type="button" variant={isVideo ? 'default' : 'outline'} onClick={() => setFormData({ ...formData, media_type: 'video' })} className={isVideo ? 'bg-forest' : ''}>
                <Video className="w-4 h-4 mr-2" /> Wideo
              </Button>
            </div>
          </div>
          
          {/* Source */}
          <div>
            <Label>{isVideo ? 'URL wideo' : 'Zdjęcie'}</Label>
            {!isVideo && (
              <div className="flex gap-2 mt-1">
                <Input 
                  value={formData.src} 
                  onChange={(e) => setFormData({ ...formData, src: e.target.value })}
                  placeholder="URL lub prześlij plik"
                  className="flex-1"
                />
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                  <Upload className="w-4 h-4 mr-1" /> {isUploading ? '...' : 'Prześlij'}
                </Button>
              </div>
            )}
            {isVideo && (
              <Input 
                value={formData.src} 
                onChange={(e) => setFormData({ ...formData, src: e.target.value })}
                placeholder="https://youtube.com/watch?v=..."
                className="mt-1"
              />
            )}
            {formData.src && !isVideo && (
              <img src={formData.src} alt="Podgląd" className="mt-2 w-full h-40 object-cover rounded" />
            )}
          </div>
          
          {/* Title */}
          <div>
            <Label>Tytuł</Label>
            <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="np. Widok na las" className="mt-1" />
          </div>
          
          {/* Alt Text for SEO */}
          <div>
            <Label>Tekst alternatywny (SEO)</Label>
            <Input 
              value={formData.alt_text} 
              onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })} 
              placeholder="Opis zdjęcia dla wyszukiwarek i czytników ekranu" 
              className="mt-1" 
            />
            <p className="text-xs text-forest/60 mt-1">Opisz co widać na zdjęciu - pomaga w SEO i dostępności</p>
          </div>
          
          {/* Category - using dynamic categories */}
          <div>
            <Label>Kategoria</Label>
            <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Wybierz kategorię" /></SelectTrigger>
              <SelectContent>
                {galleryCategories.length > 0 ? (
                  galleryCategories.map(cat => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.label}</SelectItem>
                  ))
                ) : (
                  <SelectItem value="" disabled>Brak kategorii - dodaj w panelu</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          
          {/* Show on homepage */}
          <div className="flex items-center gap-2 p-3 bg-sand/50 rounded">
            <Switch checked={formData.show_on_homepage} onCheckedChange={(checked) => setFormData({ ...formData, show_on_homepage: checked })} />
            <div>
              <Label>Pokaż na stronie głównej</Label>
              <p className="text-xs text-forest/60">Zaznacz aby wyświetlić w sekcji Galeria</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anuluj</Button>
          <Button onClick={() => onSave(formData, isNew)} className="bg-forest hover:bg-forest-light">{isNew ? 'Dodaj' : 'Zapisz'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDashboard;
